import React, { useState, useEffect } from 'react';
import { User, Target, Scale, Ruler, Calendar, Save, RefreshCw } from 'lucide-react';
import api from '../api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [profile, setProfile] = useState({
    age: '',
    gender: '',
    height: '',
    weight: '',
    goalType: '',
    goalSpeed: ''
  });
  const [targets, setTargets] = useState({
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
  });
  const [calculatedTargets, setCalculatedTargets] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch profile data
      const profileRes = await api.get('/auth/profile');
      if (profileRes.data && profileRes.data.profile) {
        setProfile(profileRes.data.profile);
      }

      // Fetch current targets
      const targetsRes = await api.get('/auth/targets');
      if (targetsRes.data && targetsRes.data.targets) {
        setTargets(targetsRes.data.targets);
        setCalculatedTargets(targetsRes.data.targets);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleTargetsChange = (e) => {
    setTargets({ ...targets, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await api.put('/auth/profile', {
        age: parseInt(profile.age),
        gender: profile.gender,
        height: parseInt(profile.height),
        weight: parseFloat(profile.weight),
        goalType: profile.goalType,
        goalSpeed: parseFloat(profile.goalSpeed)
      });

      if (response.data.targets) {
        setCalculatedTargets(response.data.targets);
        setTargets(response.data.targets);
      }

      setMessage('Profile updated successfully!');
    } catch (err) {
      setMessage('Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleTargetsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await api.post('/auth/targets', {
        calories: parseInt(targets.calories),
        protein: parseInt(targets.protein),
        carbs: parseInt(targets.carbs),
        fat: parseInt(targets.fat)
      });
      setMessage('Targets saved successfully!');
    } catch (err) {
      setMessage('Failed to save targets.');
    } finally {
      setLoading(false);
    }
  };

  const resetToCalculated = () => {
    if (calculatedTargets) {
      setTargets(calculatedTargets);
      setMessage('Targets reset to calculated values.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8">
            <h1 className="text-3xl font-bold text-white flex items-center">
              <User className="w-8 h-8 mr-3" />
              Settings
            </h1>
            <p className="text-blue-100 mt-2">Manage your profile and macro targets</p>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User className="w-4 h-4 inline mr-2" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('targets')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'targets'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Target className="w-4 h-4 inline mr-2" />
                Macro Targets
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {message && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.includes('Failed') 
                  ? 'bg-red-50 text-red-700 border border-red-200' 
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {message}
              </div>
            )}

            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Age */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-2" />
                      Age
                    </label>
                    <input
                      type="number"
                      value={profile.age}
                      onChange={(e) => handleProfileChange('age', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      min="13"
                      max="120"
                    />
                  </div>

                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Gender
                    </label>
                    <select
                      value={profile.gender}
                      onChange={(e) => handleProfileChange('gender', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  {/* Height */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Ruler className="w-4 h-4 inline mr-2" />
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      value={profile.height}
                      onChange={(e) => handleProfileChange('height', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      min="100"
                      max="250"
                    />
                  </div>

                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Scale className="w-4 h-4 inline mr-2" />
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={profile.weight}
                      onChange={(e) => handleProfileChange('weight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      min="30"
                      max="300"
                    />
                  </div>

                  {/* Goal Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Target className="w-4 h-4 inline mr-2" />
                      Goal
                    </label>
                    <select
                      value={profile.goalType}
                      onChange={(e) => handleProfileChange('goalType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select goal</option>
                      <option value="lose">Lose Weight</option>
                      <option value="maintain">Maintain Weight</option>
                      <option value="gain">Gain Weight</option>
                    </select>
                  </div>

                  {/* Goal Speed */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Goal Speed (kg/week)
                    </label>
                    <select
                      value={profile.goalSpeed}
                      onChange={(e) => handleProfileChange('goalSpeed', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select speed</option>
                      <option value="0.25">0.25 kg/week (Slow)</option>
                      <option value="0.5">0.5 kg/week (Moderate)</option>
                      <option value="1.0">1.0 kg/week (Fast)</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Update Profile
                </button>
              </form>
            )}

            {activeTab === 'targets' && (
              <div>
                {calculatedTargets && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">Calculated Targets (Based on Profile)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-blue-700">
                      <div>Calories: {calculatedTargets.calories}</div>
                      <div>Protein: {calculatedTargets.protein}g</div>
                      <div>Carbs: {calculatedTargets.carbs}g</div>
                      <div>Fat: {calculatedTargets.fat}g</div>
                    </div>
                    <button
                      onClick={resetToCalculated}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Reset to calculated values
                    </button>
                  </div>
                )}

                <form onSubmit={handleTargetsSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Calories
                      </label>
                      <input
                        type="number"
                        name="calories"
                        value={targets.calories}
                        onChange={handleTargetsChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        min="1000"
                        max="5000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Protein (g)
                      </label>
                      <input
                        type="number"
                        name="protein"
                        value={targets.protein}
                        onChange={handleTargetsChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        min="30"
                        max="300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Carbs (g)
                      </label>
                      <input
                        type="number"
                        name="carbs"
                        value={targets.carbs}
                        onChange={handleTargetsChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        min="20"
                        max="500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fat (g)
                      </label>
                      <input
                        type="number"
                        name="fat"
                        value={targets.fat}
                        onChange={handleTargetsChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        min="20"
                        max="200"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Targets
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
